import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'

type CircuitBreakerStatus = 'CLOSED' | 'OPEN'

const MAX_FAILURES = 1

export type ErrorType = 'RateLimitError' | 'ServiceUnavailableError' | 'Unknown'

export type CircuitBreakerData = {
  status: CircuitBreakerStatus
  nextAttemptTime: number | null
  failureCount: number
  lastErrorType?: ErrorType
}

const INITIAL_DATA: CircuitBreakerData = {
  status: 'CLOSED',
  nextAttemptTime: null,
  failureCount: 0,
}

const CIRCUIT_BREAKER_EVENT = 'offline_cache.circuit_breaker'

export class CircuitBreaker {
  #key: string
  #data: CircuitBreakerData
  #sendAnalyticsEvent: SendAnalyticsEventFunction | null = null

  constructor(key: string, sendAnalyticsEvent: SendAnalyticsEventFunction) {
    this.#key = key
    this.#sendAnalyticsEvent = sendAnalyticsEvent
    this.#data = INITIAL_DATA
  }

  reportFailure = (errorType: ErrorType, retryAfter?: number, resetTime?: number) => {
    if (this.#data.lastErrorType && this.#data.lastErrorType !== errorType) {
      this.#resetDataForNewErrorType(errorType)
    }
    switch (errorType) {
      case 'RateLimitError':
        return this.#handleRateLimitError(retryAfter, resetTime)
      case 'ServiceUnavailableError':
        return this.#handleServiceUnavailableError()
      default:
        return
    }
  }

  reportSuccess = () => {
    const newData: CircuitBreakerData = {
      status: 'CLOSED',
      nextAttemptTime: null,
      failureCount: 0,
    }
    this.#data = newData
    this.#sendAnalyticsEvent?.(CIRCUIT_BREAKER_EVENT, 'success_reported')
  }

  getStatus = () => {
    if (this.#data.status === 'CLOSED') {
      return 'CLOSED'
    }
    // eslint-disable-next-line no-console
    console.log('nextAttemptTime:', this.#data.nextAttemptTime, 'currentTime:', Date.now())
    const currentTime = Date.now()
    if (this.#data.nextAttemptTime && currentTime >= this.#data.nextAttemptTime) {
      // Reset circuit breaker
      const newData: CircuitBreakerData = {
        status: 'CLOSED',
        nextAttemptTime: null,
        failureCount: 0,
      }
      this.#data = newData
      return 'CLOSED'
    }
    return 'OPEN'
  }

  #handleRateLimitError = (retryAfter?: number, resetTime?: number) => {
    // Determine next attempt time
    // Priority: retryAfter > resetTime > exponential backoff
    let nextAttemptTime: number | null = null
    if (retryAfter !== undefined) {
      const retryAfterMs = retryAfter * 1000
      // Add small jitter (0-5% of the delay) to prevent thundering herd
      const jitter = Math.random() * retryAfterMs * 0.05
      nextAttemptTime = Date.now() + retryAfterMs + jitter
    } else if (resetTime !== undefined) {
      const resetTimeMs = resetTime * 1000
      // Add small jitter (0-5 seconds) to prevent thundering herd
      const jitter = Math.random() * 5000
      nextAttemptTime = resetTimeMs + jitter
    } else {
      nextAttemptTime = this.#getNextAttemptTime(this.#data.failureCount + 1)
    }

    const failureCount = (this.#data.failureCount || 0) + 1
    const newStatus = this.#getNewStatus(this.#data, failureCount) // For RateLimitError, may remain CLOSED until max failures

    const newData: CircuitBreakerData = {
      status: newStatus,
      nextAttemptTime,
      failureCount,
      lastErrorType: 'RateLimitError',
    }
    this.#data = newData

    this.#sendAnalyticsEvent?.(CIRCUIT_BREAKER_EVENT, 'error_reported', {
      error_type: 'RateLimitError',
      new_status: newStatus,
      failure_count: failureCount,
      next_attempt_time: nextAttemptTime,
    })
  }

  #handleServiceUnavailableError = () => {
    const nextAttemptTime = this.#getNextAttemptTime(this.#data.failureCount + 1)
    const failureCount = (this.#data.failureCount || 0) + 1
    const newStatus = 'OPEN' // For ServiceUnavailableError, immediately open the circuit breaker

    const newState: CircuitBreakerData = {
      status: newStatus,
      nextAttemptTime,
      failureCount,
      lastErrorType: 'ServiceUnavailableError',
    }
    this.#data = newState

    this.#sendAnalyticsEvent?.(CIRCUIT_BREAKER_EVENT, 'error_reported', {
      error_type: 'ServiceUnavailableError',
      new_status: newStatus,
      failure_count: failureCount,
      next_attempt_time: nextAttemptTime,
    })
  }

  #resetDataForNewErrorType = (errorType: ErrorType) => {
    const newData: CircuitBreakerData = {
      status: 'CLOSED',
      nextAttemptTime: null,
      failureCount: 0,
      lastErrorType: errorType,
    }
    this.#data = newData
  }

  #getNextAttemptTime = (failureCount: number) => {
    // implement exponential backoff with jitter and a max of 5 minutes
    const baseDelay = 1000 * 5 // 5 seconds
    const maxDelay = 1000 * 60 * 5 // 5 minutes
    const exponentialDelay = Math.min(baseDelay * 2 ** (failureCount - 1), maxDelay)
    // Add jitter: random value between 0-25% of the delay to prevent thundering herd
    const jitter = Math.random() * exponentialDelay * 0.25
    const delay = exponentialDelay + jitter
    return Date.now() + delay
  }

  #getNewStatus = (currentData: CircuitBreakerData, failureCount: number) => {
    if (currentData.status === 'OPEN') {
      return 'OPEN'
    }
    if (failureCount >= MAX_FAILURES) {
      return 'OPEN'
    }
    return 'CLOSED'
  }

  /**
   * Handles the response from a preheat request, updating the circuit breaker state
   * based on whether the response has errors and backoff parameters.
   *
   * @param responseHasErrors - Whether the fetch response contains errors
   * @param fetchResult - The fetch result to extract backoff parameters from
   */
  handlePreheatResponse = <T>(responseHasErrors: boolean, fetchResult: T) => {
    if (responseHasErrors) {
      const backoffParams = this.#getBackoffParams(fetchResult)
      if (backoffParams.shouldBackoff) {
        this.reportFailure(
          (backoffParams.errorType as ErrorType) ?? 'Unknown',
          backoffParams.retryAfter,
          backoffParams.resetTime,
        )
      }
    } else if (this.#data.status === 'OPEN') {
      this.reportSuccess()
    }
  }

  #getBackoffParams = <T>(
    fetchResult: T,
  ): {
    shouldBackoff: boolean
    errorType?: string
    retryAfter?: number
    resetTime?: number
  } => {
    if (!fetchResult) return {shouldBackoff: false}

    if (typeof fetchResult === 'object') {
      if (
        'extensions' in fetchResult &&
        typeof fetchResult.extensions === 'object' &&
        fetchResult.extensions !== null &&
        'backoff' in fetchResult.extensions
      ) {
        const extensions = fetchResult.extensions as {[key: string]: unknown}
        const errorType = typeof extensions.errorType === 'string' ? extensions.errorType : undefined
        const retryAfter = typeof extensions.retryAfter === 'number' ? extensions.retryAfter : undefined
        const resetTime = typeof extensions.resetTime === 'number' ? extensions.resetTime : undefined

        return {shouldBackoff: Boolean(extensions.backoff), errorType, retryAfter, resetTime}
      }
    }
    return {shouldBackoff: false}
  }
}
